import {
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  makeStyles,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import DeleteIcon from '@material-ui/icons/Delete';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import MoreIcon from '@material-ui/icons/MoreVert';
import PostAddIcon from '@material-ui/icons/PostAdd';
import { findByKey } from '@thalesrc/js-utils';
import React, { useCallback, useContext, useState } from 'react';

import { EmergencyCasePlanControllerService, EmergencyCasePlanDto } from '../../api/client';
import DeleteDialogIcon from '../../Common/DeleteDialogIcon';
import IndicatorWrapper from '../../Common/IndicatorWrapper';
import Toolbar from '../../Common/Toolbar';
import { AlertContext } from '../../context/alert.context';
import { DialogContext } from '../../context/dialog.context';
import { ResourceContext } from '../../context/resource.context';
import { useFetch } from '../../utils';
import CasePlansForm from './CasePlansForm';
import FilesAddForm from './Files';

const useStyles = makeStyles(theme => ({
  grow: {
    flexGrow: 1,
  },
  sectionDesktop: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
    },
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
}));

function nextMonths(date: Date, months: number) {
  const d = date.getDate();
  date.setMonth(date.getMonth() + months);
  if (date.getDate() !== d) {
    date.setDate(0);
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export default function EmergencyCasePlans() {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
  const openPopover = useCallback(({ target }) => setAnchorEl(target as HTMLElement), []);
  const closePopover = useCallback(() => setAnchorEl(null), []);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const { data, fetch } = useFetch(() => EmergencyCasePlanControllerService.getAllEmergencyCasePlans(), [], { setLoading });
  const { companies } = useContext(ResourceContext);
  const { openWarnDialog } = useContext(DialogContext);
  const [fileDialogOpened, setFileDialogOpened] = useState(false);
  const [fileSelectedData, setFileSelectedData] = useState(null);

  const fileOnClose = useCallback(() => {
    setFileDialogOpened(false);
    setFileSelectedData(null);
  }, [setFileDialogOpened, setFileSelectedData]);

  const fileOnOpen = useCallback(
    (item: EmergencyCasePlanDto) => {
      setFileSelectedData(item);
      setFileDialogOpened(true);
    },
    [setFileSelectedData, setFileDialogOpened]
  );
  const { showMessage } = useContext(AlertContext);
  const showOpenDialog = useCallback(() => {
    closePopover();
    setOpen(true);
  }, [setOpen, closePopover]);

  const closeDialog = useCallback(
    async (success: boolean) => {
      if (success) {
        await fetch();
      }
      setSelectedData(null);
      setOpen(false);
    },
    [setOpen, fetch]
  );

  const onEdit = useCallback(
    (item: any) => {
      setSelectedData(item);
      setOpen(true);
    },
    [setSelectedData, setOpen]
  );

  const onDelete = useCallback(
    async (emergencyCasePlan: any) => {
      const submitted = await openWarnDialog({
        text: `${emergencyCasePlan.label} - isimli kaydını silmek istediğinize emin misiniz ?`,
        Icon: <DeleteDialogIcon />,
      });
      try {
        if (submitted) {
          await EmergencyCasePlanControllerService.deleteEmployeeEmergencyCasePlan(emergencyCasePlan.id);
          showMessage('Silme işlemi başarılı.', 'success', 'info');
          await fetch();
        }
      } catch (e) {
        showMessage('Silme işleminde sorun oluştu.', 'error', 'info');
      }
    },
    [fetch, openWarnDialog, showMessage]
  );

  return (
    <>
      <Toolbar color="secondary">
        <Typography variant="h6">Acil Durum Planları</Typography>
        <span className={classes.grow} />
        <div className={classes.sectionDesktop}>
          <IconButton color="inherit" onClick={openPopover}>
            <MoreIcon />
          </IconButton>
          <Popover
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 35, horizontal: -175 }}
            keepMounted
            open={!!anchorEl}
            onClose={closePopover}>
            <List>
              <ListItem onClick={showOpenDialog} className="cursor-pointer">
                <ListItemIcon>
                  <PostAddIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="inherit">Yeni Acil Durum Planı</Typography>
              </ListItem>
            </List>
          </Popover>
        </div>
      </Toolbar>
      <div className="p-1">
        <IndicatorWrapper data={data} loading={loading} errorMessage="Listelenecek  Kayıt Bulunamadı">
          <Table className={classes.table} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 'bold', color: 'gray' }} align="left">
                  {' '}
                  Şirket
                </TableCell>
                <TableCell style={{ fontWeight: 'bold', color: 'gray' }} align="left">
                  Acil Durum Plan İsmi
                </TableCell>
                <TableCell style={{ fontWeight: 'bold', color: 'gray' }} align="center">
                  Gerçekleştirme Tarihi
                </TableCell>
                <TableCell style={{ fontWeight: 'bold', color: 'gray' }} align="left">
                  Geçerlilik Süresi
                </TableCell>
                <TableCell style={{ fontWeight: 'bold', color: 'gray' }} align="center">
                  Son Geçerlilik Tarihi
                </TableCell>
                <TableCell style={{ fontWeight: 'bold', color: 'gray' }} align="center">
                  Dosya
                </TableCell>
                <TableCell style={{ fontWeight: 'bold', color: 'gray' }} align="right">
                  İşlemler
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.map(row => (
                <TableRow hover key={row?.id}>
                  <TableCell align="left">{findByKey(companies, 'id', row?.companyId)?.companyName}</TableCell>
                  <TableCell align="left">{row?.label}</TableCell>
                  <TableCell align="center">{row?.executionDate?.split('-')?.reverse()?.join('-')}</TableCell>
                  <TableCell align="center">{`${row?.expirationTimeInMonths} Ay`}</TableCell>
                  <TableCell align="center">
                    {nextMonths(new Date(row?.executionDate), row?.expirationTimeInMonths)?.split('-')?.reverse()?.join('-')}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton onClick={() => fileOnOpen(row)}>
                      <FileCopyIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell align="right">
                    <div>
                      <IconButton onClick={() => onEdit(row)} edge="end" aria-label="edit">
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => onDelete(row)} edge="end" aria-label="delete">
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </IndicatorWrapper>
      </div>
      <CasePlansForm fetch={fetch} open={open} onClose={closeDialog} data={selectedData} />
      <FilesAddForm open={fileDialogOpened} onClose={fileOnClose} data={fileSelectedData} />
    </>
  );
}
